import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Key, Plus, Shield, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

export const MDPPage = () => {
  return (
    <div className="min-h-screen bg-[#1A1A2E] px-4 py-6" data-testid="mdp-page">
      <div className="max-w-4xl mx-auto">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#7EC8C8] mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={18} />
          <span>Retour au dashboard</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-[#FF6B6B]/20 flex items-center justify-center">
              <Key className="w-6 h-6 text-[#FF6B6B]" />
            </div>
            <h1 
              className="text-3xl font-bold text-[#F8F9FA]"
              style={{ fontFamily: 'Outfit' }}
            >
              Mots de passe
            </h1>
          </div>
          <p className="text-[#94A3B8] mb-8">
            Gère tes identifiants et accès aux sites administratifs en toute sécurité.
          </p>
        </motion.div>

        {/* Security score */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-gradient-to-r from-[#FF6B6B]/10 to-[#7EC8C8]/10 border-[#7EC8C8]/20 p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#94A3B8] mb-1">Score de sécurité</p>
                <p className="text-4xl font-bold text-[#F8F9FA]">--</p>
                <p className="text-sm text-[#94A3B8]">Ajoute des identifiants pour voir ton score</p>
              </div>
              <Shield className="w-16 h-16 text-[#7EC8C8] opacity-50" />
            </div>
          </Card>
        </motion.div>

        {/* Quick actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8"
        >
          <Card className="bg-[#1A1A2E]/50 border-[#7EC8C8]/20 p-5 hover:border-[#7EC8C8]/40 transition-colors cursor-pointer">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#7EC8C8]/20 flex items-center justify-center">
                <Plus className="w-5 h-5 text-[#7EC8C8]" />
              </div>
              <div>
                <h4 className="text-[#F8F9FA] font-medium">Ajouter un site</h4>
                <p className="text-sm text-[#94A3B8]">impots.gouv.fr, ameli.fr...</p>
              </div>
            </div>
          </Card>
          <Card className="bg-[#1A1A2E]/50 border-[#7EC8C8]/20 p-5 hover:border-[#7EC8C8]/40 transition-colors cursor-pointer">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#4ECDC4]/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#4ECDC4]" />
              </div>
              <div>
                <h4 className="text-[#F8F9FA] font-medium">Vérifier mes MDP</h4>
                <p className="text-sm text-[#94A3B8]">Détecter les MDP faibles</p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Empty state */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-[#1A1A2E]/50 border-[#7EC8C8]/20 border-dashed p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[#FF6B6B]/10 flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-[#FF6B6B]" />
            </div>
            <h3 className="text-xl font-semibold text-[#F8F9FA] mb-2" style={{ fontFamily: 'Outfit' }}>
              Tes identifiants en sécurité
            </h3>
            <p className="text-[#94A3B8] mb-6 max-w-md mx-auto">
              Stocke tes accès aux sites administratifs (impots.gouv.fr, ameli.fr, CAF...) de manière sécurisée.
            </p>
            <Button
              className="bg-[#FF6B6B] hover:bg-[#ff5252] text-white"
              data-testid="add-password"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un identifiant
            </Button>
          </Card>
        </motion.div>

        {/* Warning */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8"
        >
          <Card className="bg-[#FF6B6B]/5 border-[#FF6B6B]/20 p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#FF6B6B] mt-0.5" />
              <div>
                <h4 className="text-[#F8F9FA] font-medium mb-1">Important</h4>
                <p className="text-sm text-[#94A3B8]">
                  Tes données sont chiffrées de bout en bout. Skipya n'a jamais accès à tes mots de passe en clair.
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default MDPPage;
